(() => {
  "use strict";

  const CACHE_KEY = "peguu:materials-cache:v2";
  const DEFAULT_TIMEOUT_MS = 12000;
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAYS_MS = [0, 500, 1000];

  let memoryCache = null;
  let inFlightPromise = null;

  class MaterialsRequestError extends Error {
    constructor(message, details = {}) {
      super(message);
      this.name = "MaterialsRequestError";
      Object.assign(this, details);
    }
  }

  const getDefaultUrl = () => {
    return (
      window.PeguuApi?.endpoint?.("materialsSummary") ||
      "/api/materials/public-products-summary"
    );
  };

  const readSessionCache = () => {
    try {
      const stored = sessionStorage.getItem(CACHE_KEY);

      if (!stored) {
        return null;
      }

      const materials = JSON.parse(stored);

      return Array.isArray(materials) ? materials : null;
    } catch (error) {
      console.warn(
        "Peguu Materials: no fue posible leer la caché de sesión.",
        error
      );

      return null;
    }
  };

  const saveSessionCache = (materials) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(materials));
    } catch (error) {
      console.warn(
        "Peguu Materials: no fue posible guardar la caché de sesión.",
        error
      );
    }
  };

  const sleep = (delay) => {
    if (!delay) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      window.setTimeout(resolve, delay);
    });
  };

  const requestOnce = async (url, timeoutMs) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      timeoutMs
    );

    try {
      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json"
        },
        signal: controller.signal
      });

      if (!response.ok) {
        const isServerError = response.status >= 500;

        throw new MaterialsRequestError(
          `La API respondió con el estado ${response.status}`,
          {
            kind: isServerError ? "http-5xx" : "http-4xx",
            status: response.status,
            retryable: isServerError
          }
        );
      }

      const payload = await response.json();

      if (!Array.isArray(payload?.data)) {
        throw new MaterialsRequestError(
          "La API no devolvió una lista válida de materiales.",
          {
            kind: "invalid-response",
            retryable: false
          }
        );
      }

      return payload.data;
    } catch (error) {
      if (error instanceof MaterialsRequestError) {
        throw error;
      }

      if (error?.name === "AbortError") {
        throw new MaterialsRequestError(
          `La solicitud de materiales superó el tiempo límite de ${timeoutMs / 1000} segundos.`,
          {
            kind: "timeout",
            retryable: true
          }
        );
      }

      throw new MaterialsRequestError(
        "No fue posible conectar con la API de materiales.",
        {
          kind: "network",
          retryable: true,
          cause: error
        }
      );
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const fetchWithRetries = async (url, timeoutMs) => {
    let lastError = null;
    let attempts = 0;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      await sleep(RETRY_DELAYS_MS[attempt]);
      attempts = attempt + 1;

      try {
        return await requestOnce(url, timeoutMs);
      } catch (error) {
        lastError = error;

        if (!error.retryable || attempt === MAX_ATTEMPTS - 1) {
          break;
        }
      }
    }

    throw new MaterialsRequestError(
      lastError?.message || "No fue posible cargar los materiales.",
      {
        kind: lastError?.kind || "unknown",
        status: lastError?.status,
        attempts,
        retryable: false,
        cause: lastError
      }
    );
  };

  const getCachedMaterials = () => {
    if (Array.isArray(memoryCache)) {
      return memoryCache;
    }

    const sessionMaterials = readSessionCache();

    if (Array.isArray(sessionMaterials)) {
      memoryCache = sessionMaterials;
      return memoryCache;
    }

    return null;
  };

  const getMaterials = (options = {}) => {
    const force = options.force === true;
    const url = String(options.url || getDefaultUrl()).trim();
    const timeoutMs = Number(options.timeoutMs) || DEFAULT_TIMEOUT_MS;

    if (inFlightPromise) {
      return inFlightPromise;
    }

    if (!force) {
      const cachedMaterials = getCachedMaterials();

      if (cachedMaterials) {
        return Promise.resolve(cachedMaterials);
      }
    }

    inFlightPromise = fetchWithRetries(url, timeoutMs)
      .then((materials) => {
        memoryCache = materials;
        saveSessionCache(materials);
        return materials;
      })
      .catch((error) => {
        const fallbackMaterials = getCachedMaterials();

        if (fallbackMaterials) {
          console.warn(
            "Peguu Materials: la API falló; se utilizará la última lista válida en caché.",
            {
              kind: error.kind,
              status: error.status,
              attempts: error.attempts
            }
          );

          return fallbackMaterials;
        }

        console.error(
          "Peguu Materials: no fue posible cargar los materiales después de los reintentos.",
          {
            kind: error.kind,
            status: error.status,
            attempts: error.attempts
          }
        );

        throw error;
      })
      .finally(() => {
        inFlightPromise = null;
      });

    return inFlightPromise;
  };

  window.PeguuMaterials = Object.freeze({
    getMaterials,
    clearMemoryCache() {
      memoryCache = null;
    }
  });
})();
